import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { LoadAccountRegistrations, LoadPersonalData } from '@store/actions';
import * as fromRootStore from '@store/index';
import { selectAccountRegistrationsLoaded } from '@store/selectors/account-registrations/account-registrations.selectors';
import {
  selectAuthCheckCompleted,
  selectIsAuthenticated,
} from '@store/selectors/auth/auth.selectors';
import { selectMfePersonalInfoClientIsLoaded } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectPersonalDataLoaded } from '@store/selectors/personal-data/personal-data.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, Observable, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
@Injectable()
export class ClientDataGuard {
  constructor(
    private globalStore: Store<fromRootStore.State>,
    private cookieService: CookieService,
  ) {}

  canActivate(): Observable<boolean> {
    // Wait for authentication check to complete before proceeding
    return this.globalStore.select(selectAuthCheckCompleted).pipe(
      withLatestFrom(this.globalStore.select(selectIsAuthenticated)),
      filter(([completed, _isAuthenticated]) => completed),
      take(1),
      switchMap(([_completed, isAuthenticated]) => {
        // If not authenticated, bail out early without making API calls
        if (!isAuthenticated) {
          return of(false);
        }
        // User is authenticated, proceed with normal ClientGuard logic
        this.loadAccountRegistrationsIfNeeded();
        if (this.cookieService.get('routeGuard') === 'OFF') {
          this.globalStore.dispatch(LoadPersonalData());
          return of(true);
        }
        return this.isPersonalDataLoaded().pipe(
          switchMap(() => of(true)),
          catchError(() => of(false)),
        );
      }),
      catchError(() => of(false)),
    );
  }

  loadAccountRegistrationsIfNeeded(): void {
    this.globalStore
      .select(selectAccountRegistrationsLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.globalStore.dispatch(LoadAccountRegistrations());
        }
      });
  }

  isPersonalDataLoaded(): Observable<boolean> {
    return this.globalStore.select(selectPersonalDataLoaded).pipe(
      tap((loaded) => {
        if (!loaded) {
          this.globalStore.dispatch(LoadPersonalData());
        }
      }),
      switchMap(() =>
        this.globalStore.select(selectMfePersonalInfoClientIsLoaded).pipe(
          filter((isLoaded) => isLoaded),
          take(1),
        ),
      ),
    );
  }
}
